/**
 * On-demand English -> French translation for the assistant's opt-in French panel.
 *
 * Strategy: prefer the browser's built-in, on-device Translator API (Chrome /
 * Edge 138+) — free, instant, private, no server call. When it isn't available
 * (Safari, iOS, Firefox), fall back to the server, which translates with Claude
 * via the same Netlify function used for answers.
 */

import { api } from '../config';

// Cache the built-in translator so its (small) on-device model loads once.
let builtinPromise = null;

// Resolve the built-in Translator API across its shipped shapes, or null if the
// browser doesn't offer on-device en->fr translation.
async function getBuiltinTranslator() {
  if (builtinPromise) return builtinPromise;

  builtinPromise = (async () => {
    try {
      if (typeof window === 'undefined' || typeof window.Translator === 'undefined') {
        return null;
      }
      const T = window.Translator;
      const opts = { sourceLanguage: 'en', targetLanguage: 'fr' };
      if (typeof T.availability === 'function') {
        const status = await T.availability(opts);
        if (status === 'unavailable') return null;
      }
      return await T.create(opts);
    } catch (e) {
      return null; // treat any capability error as "not available"
    }
  })();

  return builtinPromise;
}

// Server fallback: translate via the Netlify function (Claude), same endpoint
// and abuse protections as the assistant answers.
async function serverTranslate(text) {
  const response = await fetch(api.geminiEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode: 'translate', text }),
  });
  if (!response.ok) {
    throw new Error(`Translate server error: ${response.statusText}`);
  }
  const data = await response.json();
  return (data.french || data.text || '').trim();
}

/**
 * Translate English text to French. Returns '' for empty input. Throws only if
 * both the built-in translator and the server fallback fail.
 */
export async function translateToFrench(text) {
  const clean = (text || '').trim();
  if (!clean) return '';

  const builtin = await getBuiltinTranslator();
  if (builtin) {
    try {
      const out = await builtin.translate(clean);
      if (out && out.trim()) return out.trim();
    } catch (e) {
      // fall through to the server on any runtime failure
    }
  }
  return serverTranslate(clean);
}

/**
 * Best-effort warmup: start preparing the on-device translator (which may
 * download a small model) before the user actually clicks. No-op when the
 * built-in API is absent — the server fallback needs no warmup.
 */
export function warmUpTranslator() {
  getBuiltinTranslator().catch(() => {});
}
